import React from "react";
import PropTypes from "prop-types";
import { twMerge } from "tailwind-merge";

const BASE = "transition-all duration-300";

const MAPS = {
  borderStyle: {
    dashed: "border-dashed",
    solid: "border-solid",
  },
  borderColor: {
    error600: "border-[1.5px] border-error600",
    gray200: "border-[1.5px] border-gray200",
    gray400: "border-[1.5px] border-gray400",
    gray600: "border-[1.5px] border-gray600",
    primary600: "border-[1.5px] border-primary600",
    primary800: "border-[1.5px] border-primary800",
    success600: "border-[1.5px] border-success600",
    warning600: "border-[1.5px] border-warning600",
    pink700: "border-[1.5px] border-pink700",
    blue700: "border-[1.5px] border-blue700",
    grayTrue800: "border-[1.5px] border-grayTrue800",
  },
  color: {
    error25: "bg-error25",
    error50: "bg-error50",
    gray50: "bg-gray50",
    gray100: "bg-gray100",
    gray400: "bg-gray400",
    pink50: "bg-pink50",
    blue50: "bg-blue50",
    primary50: "bg-primary50",
    success25: "bg-success25",
    success50: "bg-success50",
    warning25: "bg-warning25",
    warning50: "bg-warning50",
    white: "bg-white",
    grayTrue800: "bg-grayTrue800",
  },
  rounded: {
    "2xl": "rounded-2xl",
    "3xl": "rounded-3xl",
    full: "rounded-full",
    lg: "rounded-lg",
    md: "rounded-md",
    sm: "rounded-sm",
    xl: "rounded-xl",
  },
};

function Card({
  as,
  children,
  className,
  isDisabled = false,
  onClick,
  borderColor,
  color = "white",
  borderStyle = "solid",
  rounded = "2xl",
  ...rest
}) {
  const isClickable = !!onClick && !isDisabled;
  const Component = as || (isClickable ? "button" : "div");

  const classes = twMerge(
    BASE,
    MAPS.color[color],
    borderColor && MAPS.borderColor[borderColor],
    MAPS.rounded[rounded],
    isClickable && "w-full cursor-pointer text-start active:scale-[0.99]",
    isDisabled && "pointer-events-none opacity-40",
    className
  );

  return (
    <Component
      className={classes}
      onClick={isClickable ? onClick : undefined}
      type={Component === "button" ? "button" : undefined}
      style={{
        borderStyle: borderColor
          ? borderStyle === "solid"
            ? "solid"
            : "dashed"
          : undefined,
      }}
      {...rest}
    >
      {children}
    </Component>
  );
}

Card.propTypes = {
  as: PropTypes.oneOf(["button", "div"]),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  isDisabled: PropTypes.bool,
  onClick: PropTypes.func,
  borderStyle: PropTypes.oneOf(["dashed", "solid"]),
  borderColor: PropTypes.oneOf([
    "error600",
    "gray200",
    "gray400",
    "gray600",
    "primary600",
    "success600",
    "warning600",
  ]),
  color: PropTypes.oneOf([
    "error50",
    "gray50",
    "gray100",
    "pink50",
    "primary50",
    "success50",
    "warning50",
    "white",
  ]),
  rounded: PropTypes.oneOf(["2xl", "3xl", "full", "lg", "md", "sm", "xl"]),
};

export default Card;
